import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, ExternalLink, FileText, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Table } from '@/components';
import { orderApi } from '@/services/orderApi';
import { Order } from '@/types/order';

type InvoiceStatusFilter = 'all' | 'ready_to_invoice' | 'processing' | 'failed' | 'invoiced';

const INVOICE_STATUS_LABELS: Record<string, string> = {
  none: 'Fatura Beklenmiyor',
  ready_to_invoice: 'Fatura Kesilmeye Hazır',
  processing: 'Kesiliyor',
  invoiced: 'Fatura Kesildi',
  failed: 'Hata Aldı',
  cancelled: 'İptal Edildi',
  refund_pending: 'İade Bekliyor',
};

const INVOICE_STATUS_COLORS: Record<string, string> = {
  none: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  ready_to_invoice: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  processing: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  invoiced: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  refund_pending: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
};

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  e_invoice: 'e-Fatura',
  e_archive: 'e-Arşiv',
};

export const InvoiceQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatusFilter>('ready_to_invoice');

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await orderApi.getOrders({
        status: 'delivered',
        invoiceStatus: invoiceStatus === 'all' ? undefined : invoiceStatus,
        searchQuery: searchQuery || undefined,
        limit: 100,
      });
      setOrders(response.orders || []);
    } catch (error) {
      console.error('Invoice queue load error:', error);
      toast.error('Fatura kuyruğu yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [invoiceStatus, searchQuery]);

  const stats = useMemo(() => {
    return orders.reduce(
      (accumulator, order) => {
        const key = order.invoiceLifecycle?.status || 'none';
        if (key in accumulator) {
          accumulator[key as keyof typeof accumulator] += 1;
        }
        return accumulator;
      },
      {
        ready_to_invoice: 0,
        processing: 0,
        failed: 0,
        invoiced: 0,
      }
    );
  }, [orders]);

  const tableData = orders.map((order) => ({
    id: order.id,
    orderNo: order.orderNo || '-',
    recipient: order.recipientInfo?.fullName || '-',
    invoiceType: order.invoiceLifecycle?.documentType
      ? DOCUMENT_TYPE_LABELS[order.invoiceLifecycle.documentType] || order.invoiceLifecycle.documentType
      : '-',
    invoiceStatus: order.invoiceLifecycle?.status || 'none',
    invoiceNumber: order.invoiceLifecycle?.invoiceNumber || '-',
    eligibleAt: order.invoiceLifecycle?.eligibleAt
      ? new Date(order.invoiceLifecycle.eligibleAt).toLocaleString('tr-TR')
      : '-',
    lastAttemptAt: order.invoiceLifecycle?.lastAttemptAt
      ? new Date(order.invoiceLifecycle.lastAttemptAt).toLocaleString('tr-TR')
      : '-',
    total: `₺${order.totalPrice.toFixed(2)}`,
    invoiceUrl: order.invoiceLifecycle?.invoiceUrl || '',
    lastError: order.invoiceLifecycle?.lastError || '',
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-dark dark:text-light">Fatura Kuyruğu</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Fatura kesilmeyi bekleyen, hata alan ve kesilmiş siparişleri yönetin.</p>
        </div>
        <button
          onClick={loadOrders}
          disabled={loading}
          className="btn-secondary inline-flex items-center gap-2"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Yenile
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {[
          { key: 'ready_to_invoice', label: 'Hazır', value: stats.ready_to_invoice },
          { key: 'processing', label: 'İşleniyor', value: stats.processing },
          { key: 'failed', label: 'Hatalı', value: stats.failed },
          { key: 'invoiced', label: 'Kesildi', value: stats.invoiced },
        ].map((item) => (
          <div key={item.key} className="card">
            <p className="text-sm text-gray-600 dark:text-gray-400">{item.label}</p>
            <p className="mt-2 text-3xl font-bold text-dark dark:text-light">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="card space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_240px]">
          <div>
            <label className="mb-2 block text-sm font-medium">Ara</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Sipariş no, müşteri, email veya telefon"
              className="input-field"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium">Fatura Durumu</label>
            <select
              value={invoiceStatus}
              onChange={(event) => setInvoiceStatus(event.target.value as InvoiceStatusFilter)}
              className="input-field"
            >
              <option value="ready_to_invoice">Hazır</option>
              <option value="processing">İşleniyor</option>
              <option value="failed">Hatalı</option>
              <option value="invoiced">Kesildi</option>
              <option value="all">Tümü</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin text-4xl">⏳</div>
        </div>
      ) : (
        <Table
          headers={['Sipariş No', 'Alıcı', 'Belge Tipi', 'Fatura Durumu', 'Fatura No', 'En Erken Kesim', 'Son Deneme', 'Toplam', 'Hata']}
          data={tableData}
          renderCell={(item, header) => {
            if (header === 'Fatura Durumu') {
              return (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${INVOICE_STATUS_COLORS[item.invoiceStatus] || INVOICE_STATUS_COLORS.none}`}>
                  {INVOICE_STATUS_LABELS[item.invoiceStatus] || item.invoiceStatus}
                </span>
              );
            }

            if (header === 'Hata') {
              return item.lastError ? (
                <span className="line-clamp-2 max-w-xs text-xs text-red-600 dark:text-red-300">{item.lastError}</span>
              ) : '-';
            }

            const headerMap: Record<string, string> = {
              'Sipariş No': 'orderNo',
              'Alıcı': 'recipient',
              'Belge Tipi': 'invoiceType',
              'Fatura No': 'invoiceNumber',
              'En Erken Kesim': 'eligibleAt',
              'Son Deneme': 'lastAttemptAt',
              'Toplam': 'total',
            };

            return item[headerMap[header]] || '-';
          }}
          actions={(item) => (
            <div className="flex items-center gap-2">
              {item.invoiceUrl && (
                <a
                  href={item.invoiceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded p-2 text-green-600 transition hover:bg-green-100 dark:hover:bg-green-900"
                  title="Belgeyi aç"
                >
                  <ExternalLink size={16} />
                </a>
              )}
              <button
                onClick={() => navigate(`/admin/orders/${item.id}`)}
                className="rounded p-2 text-primary transition hover:bg-blue-100 dark:hover:bg-blue-900"
                title="Sipariş detayı"
              >
                <Eye size={16} />
              </button>
            </div>
          )}
        />
      )}

      {!loading && orders.length === 0 && (
        <div className="card py-12 text-center text-gray-500 dark:text-gray-400">
          <FileText className="mx-auto mb-3" size={28} />
          Seçili filtrelerde fatura kuyruğunda sipariş bulunamadı.
        </div>
      )}
    </div>
  );
};